/**
 * `<SubmissionProgressSection/>` — lead-side submissions progress for the persistent right panel (PR 3, #198).
 *
 * Renders a one-line "12 of 18 members submitted" with a thin progress
 * bar and a "View details →" deep link to the Submissions tab.
 *
 * Always-on per #193 (submission progress is core scheduling, not opt-in).
 *
 * **Mount conditions.**
 *   - Cycle context resolved from URL (`useParams.cycleId`).
 *   - Renders for any cycle state — submission progress is meaningful
 *     whether the cycle is open (members still submitting) or closed
 *     (lead chasing missing submissions / verifying completeness
 *     before solving).
 *
 * **Data flow.** Reuses the same `["cycle-dashboard", cycleId,
 * accountId]` query key as `<SubmissionsTab/>`'s lead view so the
 * panel mount + the tab body share a single GET. No duplicate
 * request.
 *
 * **Self-hides** on fetch error (the dashboard endpoint is lead-only;
 * a member viewer would 403 — silent hide keeps the panel calm).
 *
 * **Focal-component discipline.** The progress bar is the section
 * focal; the count + deep-link are calm supporting context.
 */
import { useQuery } from "@tanstack/react-query";
import { Users } from "lucide-react";
import { useParams } from "react-router-dom";

import { type ApiError, apiFetch } from "../api/client";
import { useCycle } from "../api/useCycle";
import { useAuth } from "../lib/AuthContext";
import { AmbientErrorNotice, isUnexpectedApiError } from "./AmbientErrorNotice";
import { AmbientPill } from "./AmbientPill";

interface DashboardSummary {
  submitted_count: number;
  total_active_members: number;
}

/**
 * The section's reads + derived counts, shared with the panel handle's
 * badge (#1920, `lib/persistentPanelBadge.ts`) so both read the one
 * `["cycle-dashboard", …]` cache entry. `showsCount` is true when the
 * section renders with members still to submit (`outstanding > 0`).
 */
export function useSubmissionProgress(cycleId: string | undefined) {
  const { accountId } = useAuth();
  const cycleQuery = useCycle(cycleId);

  // Same key as `SubmissionsTab.tsx`'s lead view so the cache hit is
  // free in either direction (panel → tab navigation, or tab → panel
  // refresh on tab swap).
  const dashboardQuery = useQuery<DashboardSummary, ApiError | Error>({
    queryKey: ["cycle-dashboard", cycleId ?? "", accountId ?? ""],
    enabled: Boolean(cycleId && accountId),
    staleTime: 30_000,
    retry: false,
    queryFn: () => apiFetch<DashboardSummary>(`/api/cycles/${cycleId}/dashboard`),
  });

  const submitted = dashboardQuery.data?.submitted_count ?? 0;
  const total = dashboardQuery.data?.total_active_members ?? 0;
  const outstanding = Math.max(0, total - submitted);
  const showsCount =
    Boolean(cycleId) &&
    !cycleQuery.isError &&
    !dashboardQuery.isError &&
    dashboardQuery.data !== undefined &&
    outstanding > 0;

  return { cycleQuery, dashboardQuery, submitted, total, outstanding, showsCount };
}

export function SubmissionProgressSection() {
  const { cycleId } = useParams<{ cycleId: string }>();
  const { cycleQuery, dashboardQuery, submitted, total } = useSubmissionProgress(cycleId);

  if (!cycleId) return null;
  // The dashboard endpoint is lead-only, but this section only mounts in
  // the lead panel — so a 401/403/5xx here means a broken session or a
  // server fault, not "no data". Surface it calmly; an expected 404
  // (cycle absent) keeps hiding.
  if (cycleQuery.isError || dashboardQuery.isError) {
    const unexpected =
      (cycleQuery.isError && isUnexpectedApiError(cycleQuery.error)) ||
      (dashboardQuery.isError && isUnexpectedApiError(dashboardQuery.error));
    if (unexpected) {
      return (
        <AmbientErrorNotice
          onRetry={() => {
            if (cycleQuery.isError) void cycleQuery.refetch();
            void dashboardQuery.refetch();
          }}
          label="Submissions"
          testId="submission-progress-section-error"
        />
      );
    }
    return null;
  }

  const pct = total === 0 ? 0 : Math.round((submitted / total) * 100);

  return (
    <AmbientPill
      variant="flat"
      title="Submissions"
      ariaLabel="Submission progress"
      detailTo={`/cycles/${cycleId}/submissions`}
      detailLabel="View details"
      detailArrow
      detailLinkClassName="flex items-center gap-1 text-xs text-fg-muted hover:text-fg"
      detailTestId="submission-progress-deep-link"
      testId="submission-progress-section"
      isLoading={dashboardQuery.isLoading}
      skeletonClassName="h-8"
      skeletonTestId="submission-progress-loading"
    >
      <div className="flex flex-col gap-1.5" data-testid="submission-progress-data">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-fg-muted" aria-hidden="true" />
          <span className="text-sm font-medium text-fg">
            {submitted} of {total} submitted
          </span>
        </div>
        {/* Focal: the progress bar. Calm green when fully in, accent
            while in-flight. The bar is the section's at-a-glance
            status — count + deep-link are supporting. */}
        <div
          className="h-1.5 w-full overflow-hidden rounded-full bg-bg-subtle"
          data-testid="submission-progress-bar"
          data-pct={pct}
        >
          <div
            className={
              pct === 100 ? "h-full bg-success transition-all" : "h-full bg-accent transition-all"
            }
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </AmbientPill>
  );
}
